/**
 * The granny flat plan page (rebuild, 21 Oct 2026; the agreed mock-up's j1 plan page and j2 "back on the
 * plan page").
 *
 * AGED CARE'S SHAPE, FROM THE SHARED PIECES (owner, 22 Oct 2026: "Yes i want this all consistent"). The chosen
 * plan on ChosenPlanCard — what is left against staying put, the Age Pension, the year's money and what is given
 * for the right; the arrangement and, with a guaranteed income, what the family receives; then the advice document
 * and the next step (the family guide, then a solicitor, or the questions left once the brief is printed); then
 * "Model something else" and the ways on as links. Every figure is the chosen card's own, as it was chosen.
 */
import React from 'react';
import { ArrowLeftRight, BookOpen, Hammer, Pencil } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import AnnuityFamilyPanel from '../../components/strategy/AnnuityFamilyPanel.tsx';
import { PlanOutOfDateBanner } from '../../components/strategy/PlanOutOfDateBanner.tsx';
import { ChosenPlanCard, DocumentAndNextStep, ModelSomethingElse, PlanPageLinks, type PlanFigure } from '../../components/strategy/ChosenPlanPage.tsx';
import { EXTRA_ALLOWABLE_AMOUNT } from '../../constants/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { ChosenGfPlan } from './gfChosenPlan.ts';
import { gfHostPhrase, gfEstateWords } from './gfPresentation.ts';
import { gfGuideProgress, openQuestionsFor } from './gfSolicitorBrief.ts';

export interface GfHubProps {
    factFind: DraftFactFind;
    plan: ChosenGfPlan;
    onChangeArrangement: () => void;
    /** Step 1 again with the answers kept: another arrangement measured the same way. */
    onModelAnother?: () => void;
    /** Step 1 empty, the questions asked again. */
    onStartFresh?: () => void;
    onAllPlans: () => void;
    onExplain: () => void;
    onRisks: () => void;
    onAdvice: () => void;
    onBuild: () => void;
    /** "Build a plan yourself": a blank plan for the same arrangement (25 Oct 2026). */
    onBuildNew?: () => void;
    /** Why the chosen plan's figures are out of date (chosenPlanRefresh.ts), or null (25 Oct 2026). */
    outOfDate?: string | null;
    onFamilyGuide: () => void;
    onPrintBrief: () => void;
    onReadGuide: () => void;
    onAssumptions: () => void;
    onHome: () => void;
}

const money = (n: number) => formatCurrency(Math.round(n));
const signed = (n: number) => `${n >= 0 ? '+' : '−'}${money(Math.abs(n))}`;
const dateWords = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : null);
const link = 'font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900';

const GfHub = ({ factFind, plan, onChangeArrangement, onModelAnother, onStartFresh, onAllPlans, onExplain, onRisks, onAdvice, onBuild, onBuildNew, outOfDate = null, onFamilyGuide, onPrintBrief, onReadGuide, onAssumptions, onHome }: GfHubProps) => {
    const v = householdVoice(factFind);
    const card = plan.inputs!.card!;
    const host = gfHostPhrase(factFind);
    const estate = gfEstateWords(factFind);
    const base = card.baseline;
    const delta = base ? card.plan.legacy - base.legacy : null;
    const yrs = `${card.horizonYears} ${card.horizonYears === 1 ? 'year' : 'years'}`;
    const cash = (n: number) => (n < 0 ? `short ${money(-n)}` : `${money(n)} left over`);
    const a = card.arrangement;
    const guide = factFind?.grannyFlat?.familyGuide || {};
    const progress = gfGuideProgress(guide);
    const brief = guide.brief?.generatedAt ? guide.brief : null;
    const openLeft = openQuestionsFor(guide).length;
    const builtByYou = card.origin === 'manual';
    const homeWords = a.home === 'rented' ? `kept, rented at about ${money(a.rentAYear)} a year`
        : a.home === 'kept' ? 'kept, empty' : a.home === 'sold' ? 'sold' : a.home === 'transferred' ? `signed over to ${host}` : null;
    const pensionChange = base ? card.plan.pension - base.pension : null;

    const figures: PlanFigure[] = [
        {
            label: 'Age Pension',
            value: money(card.plan.pension),
            unit: 'a year',
            note: pensionChange == null ? undefined : Math.abs(pensionChange) >= 1 ? `${signed(pensionChange)} a year vs staying put` : 'the same as staying put',
        },
        {
            /* "Each year, short by" when the plan falls short, as aged care's plan page says it (owner, 22 Oct 2026). */
            label: card.plan.cashflow < 0 ? 'Each year, short by' : 'Left over each year',
            value: money(Math.abs(card.plan.cashflow)),
            tone: card.plan.cashflow < 0 ? 'plain' : 'good',
            note: base ? `staying put: ${cash(base.cashflow)}` : undefined,
            testId: 'gf-hub-cashflow',
        },
        {
            label: a.given > 0 ? `Given to ${host}` : 'Paid for the right to live there',
            value: money(a.given),
            note: a.given > 0 ? (a.gift > 0 ? `${money(a.gift)} of it counts as a gift` : 'none of it counts as a gift') : 'nothing is given — just moving in',
            testId: 'gf-hub-given',
        },
    ];

    return (
        <div className="space-y-4" data-testid="gf-hub">
            <div>
                <p className="text-[13px] text-slate-500"><button type="button" onClick={onHome} className="underline decoration-dotted underline-offset-2 hover:text-slate-800">Home</button> › Granny flat</p>
                <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-brand-navy" data-testid="gf-hub-title">{v.you ? 'Your granny flat plan' : `${v.Possessive} granny flat plan`} — with {host}</h1>
                <p className="mt-1 text-[15px] leading-relaxed text-slate-600">
                    {delta != null && (delta < 0
                        ? <>{v.Possessive} estate is {money(-delta)} less than staying put after about {yrs}{a.given > 0 ? <>; {money(a.given)} of what was given is with {host}, not lost</> : null}.{' '}</>
                        : <>{v.Possessive} estate is {money(delta)} more than staying put after about {yrs}.{' '}</>)}
                    <button type="button" onClick={onChangeArrangement} className={link}>Change the arrangement</button>
                    {onStartFresh && <>{' · '}<button type="button" onClick={onStartFresh} className={link} data-testid="gf-hub-start-fresh-header">Start a new model from scratch</button></>}.
                </p>
            </div>

            {/* A chosen plan whose figures are out of date says so, with a way to choose again (25 Oct 2026). */}
            <PlanOutOfDateBanner reason={outOfDate} onChooseAgain={onAllPlans} testId="gf-hub-out-of-date" />
            <ChosenPlanCard
                testIdPrefix="gf-hub"
                chosenOn={dateWords(plan.createdAt)}
                builtByYou={builtByYou}
                title={card.words.title}
                sub={card.words.sub || undefined}
                estate={card.plan.legacy}
                years={card.horizonYears}
                estateDelta={delta}
                baseWord="staying put"
                figures={figures}
                onExplain={onExplain}
                onRisks={onRisks}
                onAllPlans={onAllPlans}
            >
                <div data-testid="gf-hub-arrangement">
                    <p className="text-[14px] font-semibold text-slate-900">The arrangement</p>
                    <ul className="mt-1 space-y-0.5 text-[14px] leading-relaxed text-slate-700">
                        <li data-testid="gf-hub-estate">In {estate} after about {yrs}: <strong className="tabular-nums text-slate-900">{money(card.plan.legacy)}</strong>{a.given > 0 ? <>, plus {money(a.given)} with {host}</> : null}.</li>
                        {a.given > 0 && <li>Centrelink&apos;s value of the right to live there: <span className="tabular-nums">{money(a.interestValue)}</span>.</li>}
                        <li>Homeowner for the Age Pension: {a.isHomeowner ? `yes — more than ${money(EXTRA_ALLOWABLE_AMOUNT)} given, so the right is not counted` : a.given > 0 ? `no — ${money(a.given)} given, not more than ${money(EXTRA_ALLOWABLE_AMOUNT)}, so the right counts as an asset` : 'no'}.</li>
                        {homeWords && <li>The home: {homeWords}.</li>}
                        <li>Starting: {a.dateEntered ? dateWords(a.dateEntered) : 'now'}.</li>
                    </ul>
                </div>
                {card.family && card.family.rows.length > 0 && (
                    <div className="mt-4">
                        <AnnuityFamilyPanel
                            factFind={factFind}
                            personName={card.family.personName}
                            amount={card.family.amount}
                            kind={card.family.kind}
                            incomeAYear={card.family.incomeAYear}
                            rows={null}
                            frozen={card.family.rows}
                            compareNote={card.family.compareNote}
                            horizonYears={card.horizonYears}
                            testId="gf-hub-family-panel"
                        />
                    </div>
                )}
            </ChosenPlanCard>

            <div data-testid="gf-hub-next">
                <DocumentAndNextStep
                    documentState={plan.soaSnapshot ? 'prepared' : 'not prepared yet'}
                    documentLine="The written record of this plan: what it assumes, what it costs, what could go wrong."
                    documentAction={<button type="button" onClick={onAdvice} className={link} data-testid="gf-hub-advice">{plan.soaSnapshot ? 'Open the advice document' : 'Get the advice document'}</button>}
                    nextTitle={brief ? 'see a solicitor' : 'the family guide, then a solicitor'}
                    nextLine={brief ? (
                        <span data-testid="gf-hub-brief">
                            Solicitor brief printed {dateWords(brief.generatedAt)}. {openLeft === 0 ? 'Every question has an answer.' : `${openLeft} ${openLeft === 1 ? 'question' : 'questions'} left for the solicitor.`}{' '}
                            <button type="button" onClick={onPrintBrief} className={link} data-testid="gf-hub-print-again">Print it again</button> · <button type="button" onClick={onFamilyGuide} className={link} data-testid="gf-hub-guide">Continue the guide</button>.
                        </span>
                    ) : (
                        <span data-testid="gf-hub-guide-progress">
                            {progress.answered} of {progress.total} questions answered.{' '}
                            <button type="button" onClick={onFamilyGuide} className={link} data-testid="gf-hub-guide">{progress.answered > 0 ? 'Continue the guide' : 'Open the guide'}</button> — it prints a brief with this plan&apos;s figures for the solicitor.
                        </span>
                    )}
                    nextAction={<button type="button" onClick={onHome} className={link}>Full action plan on Home</button>}
                />
            </div>

            {(onModelAnother || onStartFresh) && (
                <ModelSomethingElse
                    testId="gf-hub-model-something-else"
                    options={[
                        ...(onModelAnother ? [{
                            title: 'Another arrangement, same answers',
                            body: `Keeps who ${v.they} would live with and the answers to the questions. Change what is given, the home or the start.`,
                            action: 'Model another arrangement',
                            onClick: onModelAnother,
                            testId: 'gf-hub-model-another',
                        }] : []),
                        ...(onStartFresh ? [{
                            title: 'A new model from scratch',
                            body: 'Starts again at step 1 with nothing carried over, and asks the questions again so you can change any of them.',
                            action: 'Start a new model from scratch',
                            onClick: onStartFresh,
                            testId: 'gf-hub-start-fresh',
                        }] : []),
                    ]}
                />
            )}

            <PlanPageLinks
                links={[
                    { label: 'Change the arrangement', icon: <Pencil size={14} />, onClick: onChangeArrangement, testId: 'gf-hub-change' },
                    { label: 'All the plans we tested', icon: <ArrowLeftRight size={14} />, onClick: onAllPlans },
                    // Two doors (owner, 25 Oct 2026): change this plan, or start a blank one.
                    { label: builtByYou ? 'Change the plan you built' : 'Customise this plan', icon: <Hammer size={14} />, onClick: onBuild, testId: 'gf-hub-build' },
                    ...(onBuildNew ? [{ label: 'Build a plan yourself', icon: <Hammer size={14} />, onClick: onBuildNew, testId: 'gf-hub-build-new' }] : []),
                    { label: 'The family discussion guide', icon: <BookOpen size={14} />, onClick: onFamilyGuide },
                    { label: 'How granny flat arrangements work — the guide', icon: <BookOpen size={14} />, onClick: onReadGuide },
                    { label: 'Rates and assumptions', icon: <BookOpen size={14} />, onClick: onAssumptions },
                ]}
            />
        </div>
    );
};

export default GfHub;
